import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import Quill from "quill";
import 'quill/dist/quill.snow.css';


// Pick your favorite regexp for url detection e.g. https://stackoverflow.com/a/3809435/
// use at your own risk...
const QUILL_URL_REGEXP = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;

export const autoFormatUrls = (quillInstance) => {
    const text = quillInstance.getText();
    const matches = text.matchAll(QUILL_URL_REGEXP); // find all urls in text.

    // Save current cursor position
    const range = quillInstance.getSelection();
    const cursorPosition = range ? range.index : null;

    for (const match of matches) {
        const url = match[0];
        const index = match.index ?? 0; // Ensure index isn't undefined

        // Ensure URL is not already a link
        const formats = quillInstance.getFormat(index, url.length);
        if (!formats.link) {
            quillInstance.formatText(index, url.length, 'link', url, 'silent');
        }
    }

    // Restore cursor position if it was moved
    if (cursorPosition !== null) {
        quillInstance.setSelection(cursorPosition);
    }
};

const QuillEditor = forwardRef(
    ({ readOnly, defaultValue, onTextChange, onSelectionChange, placeholder = '' }, ref) => {
        const containerRef = useRef(null);
        const defaultValueRef = useRef(defaultValue);
        const onTextChangeRef = useRef(onTextChange);
        const onSelectionChangeRef = useRef(onSelectionChange);

        useLayoutEffect(() => {
            onTextChangeRef.current = onTextChange;
            onSelectionChangeRef.current = onSelectionChange;
        });

        useEffect(() => {
            ref.current?.enable(!readOnly);
        }, [ref, readOnly]);

        useEffect(() => {
            const container = containerRef.current;

            const wrapper = container.ownerDocument.createElement('div');
            wrapper.className = 'editor-wrapper';

            const editorElement = container.ownerDocument.createElement('div');
            let quill;

            if (!readOnly) {
                const toolbarElement = container.ownerDocument.createElement('div');
                toolbarElement.classList.add('ql-toolbar', 'ql-snow');

                toolbarElement.innerHTML = `
                <span class="ql-formats">
                    <button class="ql-bold"></button>
                    <button class="ql-italic"></button>
                    <button class="ql-underline"></button>
                </span>
                <span class="ql-formats">
                    <button class="ql-list" value="bullet"></button>
                </span>
            `;

                wrapper.appendChild(editorElement);
                wrapper.appendChild(toolbarElement);

                container.appendChild(wrapper);

                quill = new Quill(editorElement, {
                    theme: 'snow',
                    placeholder: placeholder,
                    modules: {
                        toolbar: {
                            container: toolbarElement,
                        },
                    },
                });
            } else {
                wrapper.appendChild(editorElement);

                container.appendChild(wrapper);

                quill = new Quill(editorElement, {
                    theme: 'snow',
                    placeholder: placeholder,
                    modules: {
                        toolbar: false,
                    },
                });
            }

            ref.current = quill;
            ref.current?.enable(!readOnly);

            if (defaultValueRef.current) {
                if (typeof defaultValueRef.current === 'object' && defaultValueRef.current.ops) {
                    quill.setContents(defaultValueRef.current);
                }
            }

            quill.on(Quill.events.TEXT_CHANGE, (delta, oldDelta, source) => {
                const editorEl = quill.root;

                const selection = quill.getSelection();
                const currentLength = quill.getLength();
                const lastOp = delta.ops[delta.ops.length - 1];

                const addedNewlineAtEnd =
                    source === 'user' &&
                    lastOp &&
                    typeof lastOp.insert === 'string' &&
                    lastOp.insert.includes('\n') &&
                    selection &&
                    selection.index >= currentLength - 2;

                if (addedNewlineAtEnd) {
                    editorEl.scrollTop = editorEl.scrollHeight;
                }

                requestAnimationFrame(() => {
                    try {
                        autoFormatUrls(quill);
                    } catch (e) {
                        console.error('Error while formatting links in text', e);
                    }
                    onTextChangeRef.current?.(delta, oldDelta, source);
                });
            });

            quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
                onSelectionChangeRef.current?.(...args);
            });

            return () => {
                ref.current = null;
                container.innerHTML = '';
            };
        }, [ref]);

        return <div ref={containerRef}></div>;
    },
);

QuillEditor.displayName = 'Editor';

export default QuillEditor;
